<script setup lang="ts">
import { defineLayoutOfProduct } from '@/utils/defineLayoutOfProduct'

import { Modification, Product } from '@/types/api/api-v3/product'
import { NAME_PARENT_COMPONENT } from '@/types/products'

type Props = {
  product: Product
  currentModification: Modification
  parentComponent: string
}

const props = defineProps<Props>()

const { isMobile } = useDevice()

const { productTitle, productImage, productImageList, setError } =
  useProductImage(props)

const slideWidth = computed(() => (isMobile ? 350 : 678))

const checkVisibleQuantity = computed(() =>
  Boolean(
    props.product.elements && props.product.category_slug?.includes('sety'),
  ),
)

const isHighLight = computed(() => {
  return props.product.tags.find((item) => item.as_highlight)
})

const isShowDiscount = computed(() => {
  return !!props.currentModification.discount
})

const getProductMicrolayouts = defineLayoutOfProduct(props.parentComponent)

const itemTypeProductItem = getProductMicrolayouts('image')
</script>

<template>
  <div class="product__image">
    <the-slider
      v-if="productImageList.length"
      class="product__slider"
      :slide-width="slideWidth"
      :space-between="10"
      :arrows="!isMobile"
      :length="productImageList.length"
      :dots-offset="8"
      dots
    >
      <nuxt-img
        v-for="image in productImageList"
        :key="image"
        :src="image"
        :alt="product.name"
        :title="productTitle"
        class="image"
        loading="lazy"
        format="webp"
        width="100%"
        height="100%"
      />
    </the-slider>

    <nuxt-img
      v-else
      :src="productImage"
      :alt="product.name"
      :title="productTitle"
      :itemprop="itemTypeProductItem"
      class="image"
      loading="lazy"
      format="webp"
      width="100%"
      height="100%"
      @error="setError(true)"
    />

    <div v-if="checkVisibleQuantity" class="product__image-quantity">
      {{ product.elements }} шт.
    </div>

    <span v-if="isShowDiscount" class="product__image-discount">
      -{{ currentModification.discount }}%
    </span>

    <template v-if="NAME_PARENT_COMPONENT.DIALOG === parentComponent">
      <div
        v-if="isHighLight"
        :style="{ 'background-color': isHighLight.hover_background_color }"
        class="product__image-sale"
      >
        {{ isHighLight.name }}
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
.product__image {
  img {
    display: block;
    width: 100%;
    border-radius: 20px;
  }

  &-tags {
    @include media('>=desktop-mini') {
      position: absolute;
      top: 0;
      left: 0;
      max-width: 100%;
      overflow: hidden;
    }
  }
}

@mixin quantity {
  position: absolute;
  right: 0;
  display: flex;
  justify-content: center;
  padding: 3px 8px;
  font-size: 12px;
  font-weight: 800;
  color: var(--ui-white);
  background: var(--ui-text-primary);
  border-radius: 200px;
}

@mixin quantity-tablet {
  width: initial;
  padding: 2px 4px;
  margin-top: 24px;
  font-size: 10px;
  line-height: 12px;
}

@mixin discount-styles {
  position: absolute;
  top: 0;
  right: 0;
  font-weight: 800;
  color: #000;
  background: var(--ui-yellow);
}

@mixin product-main-desktop {
  .product__image {
    position: relative;
    grid-area: 1 / 1 / 2 / 3;
    height: max-content;
    min-height: 187px;
    max-height: 270px;
    transition: 0.2s ease-in;

    &:hover {
      cursor: pointer;
      transition: 0.2s ease-in;
      transform: translateY(5px);
    }
  }

  .product__image-quantity {
    @include quantity;

    bottom: 18px;

    @include media('<=tablet') {
      @include quantity-tablet;
    }
  }

  .product__image-discount {
    @include discount-styles;

    padding: 6px 12px;
    font-size: 12px;
    line-height: 14px;
    border-radius: 20px;
  }
}

@mixin product-main-mobile {
  .product__image {
    position: relative;
    grid-area: 1 / 1 / 2 / 2;
    height: max-content;
    min-height: 100px;
    max-height: 270px;
    transition: 0.2s ease-in;
  }

  .product__image-quantity {
    @include quantity;

    bottom: 10px;

    @include media('<=tablet') {
      @include quantity-tablet;
    }
  }

  .product__image-discount {
    @include discount-styles;

    padding: 3px 5px;
    margin-left: 10px;
    font-size: 10px;
    line-height: 12px;
    border-radius: 20px;
  }
}

@mixin product-page {
  .product__image {
    position: relative;
    max-height: 452px;
    border-radius: 32px;

    @include media('<=desktop') {
      margin-bottom: 15px;
    }

    @include media('>=tablet', '<=desktop-mini') {
      max-width: 100%;
      max-height: 100%;
    }
  }

  .product__slider {
    margin-bottom: 15px;
  }

  .product__image-discount {
    @include discount-styles;

    padding: 6px 10px;
    font-size: 16px;
    line-height: 19px;
    border-radius: 40px;

    @include media('<desktop') {
      padding: 5px 12px;
      font-size: 12px;
      line-height: 14px;
      border-radius: 20px;
    }
  }

  .product__image-quantity {
    @include quantity;

    bottom: 18px;
    padding: 3px 12px;
    font-size: 18px;
    line-height: 29px;

    @include media('<=tablet') {
      @include quantity-tablet;
    }
  }
}

.product--main-desktop {
  @include product-main-desktop();
}

.product--main-mobile {
  @include product-main-mobile;
}

.product--page {
  @include product-page;
}
</style>
